import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTheme, type Theme } from './useTheme';

/**
 * One theme state for the whole app. The provider is mounted at the root so the
 * `prefers-color-scheme` listener inside `useTheme` is always live, regardless
 * of which route is on screen; the toggle control is just a consumer and can
 * live wherever it makes sense in the UI.
 *
 * Calling `useTheme()` directly in more than one component would give each of
 * them a separate, silently diverging copy of the state — hence the context.
 */

interface ThemeState {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { theme, toggle } = useTheme();
  const value = useMemo<ThemeState>(() => ({ theme, toggle }), [theme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}
